import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, KeyRound, FolderSearch, Globe2, ShieldAlert, Hourglass, ArrowRight } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { PageHeader, StatCard, Card, Empty } from "@/components/admin/ui";
import { CASE_STATUS_LABEL } from "@/components/StatusBadge";
import { SendFrsButton } from "@/components/admin/SendFrsButton";

const PIPELINE = ["new", "in_progress", "awaiting_records", "on_hold", "review", "report_ready", "delivered"];

export default function Overview() {
  const [s, setS] = useState<any>(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
  }, []);
  if (!s) return <div className="h-40 animate-pulse rounded-2xl bg-white" />;
  const max = Math.max(1, ...PIPELINE.map((k) => s.cases[k] || 0));
  return (
    <div>
      <PageHeader title="Overview" subtitle="Everything that needs your attention today." actions={<SendFrsButton />} />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
        <Link to="/admin/orders"><StatCard testId="stat-awaiting-approval" label="Orders awaiting approval" value={s.orders_awaiting_approval} icon={<ShoppingBag className="h-5 w-5" />} tone="teal" hint={`${s.orders_pending_payment} pending payment`} /></Link>
        <Link to="/admin/cases"><StatCard testId="stat-open-cases" label="Open cases" value={s.open_cases} icon={<FolderSearch className="h-5 w-5" />} hint={`${s.in_progress_applications} applications in progress`} /></Link>
        <Link to="/admin/codes"><StatCard testId="stat-unused-codes" label="Unused codes" value={s.codes.unused} icon={<KeyRound className="h-5 w-5" />} tone="green" hint={`${s.codes.used} used · ${s.codes.reserved} in use`} /></Link>
        <Link to="/admin/cases?status=flagged"><StatCard testId="stat-flagged-foreign" label="Foreign address reviews" value={s.flagged_foreign} icon={<Globe2 className="h-5 w-5" />} tone="amber" /></Link>
        <Link to="/admin/alerts"><StatCard testId="stat-open-alerts" label="Open security alerts" value={s.open_alerts} icon={<ShieldAlert className="h-5 w-5" />} tone="red" /></Link>
        <StatCard testId="stat-orgs" label="Organizations" value={s.organizations} icon={<Hourglass className="h-5 w-5" />} />
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Card title="Case pipeline" testId="case-pipeline" actions={<Link to="/admin/cases" className="flex items-center gap-1 text-sm font-semibold text-[#1B365D]">Open queue <ArrowRight className="h-4 w-4" /></Link>}>
          <div className="space-y-3 p-6">
            {PIPELINE.map((k) => (
              <Link key={k} to={`/admin/cases?status=${k}`} className="group flex items-center gap-4">
                <span className="w-36 shrink-0 text-sm font-semibold text-[#475569] group-hover:text-[#1B365D]">{CASE_STATUS_LABEL(k)}</span>
                <span className="h-3 flex-1 overflow-hidden rounded-full bg-[#F1EFEA]">
                  <span className="block h-full rounded-full bg-[#1B365D] transition-[width] duration-700" style={{ width: `${((s.cases[k] || 0) / max) * 100}%` }} />
                </span>
                <span className="w-8 text-right font-mono text-sm font-semibold">{s.cases[k] || 0}</span>
              </Link>
            ))}
          </div>
        </Card>
        <Card title="Recent activity" testId="recent-activity">
          {s.recent_activity.length ? (
            <ul className="divide-y divide-slate-100">
              {s.recent_activity.map((a: any) => (
                <li key={a.id} className="px-6 py-3">
                  <p className="font-mono text-[13px] font-semibold text-[#1B365D]">{a.action}</p>
                  <p className="text-xs text-[#94A3B8]">{a.actor_email} · {fmtDateTime(a.at)}</p>
                </li>
              ))}
            </ul>
          ) : <Empty text="No activity yet." />}
        </Card>
      </div>
    </div>
  );
}
